import { CreditScoreOverviewItemModel, CreditUsageOverviewItemModel, Money } from '../redux/types';
import useFetchClient from "./useFetchClient";
import { useAppDispatch } from "../redux/hooks";
import { restConfig } from "../authConfig";
import { 
  loadingCreditUsage, 
  loadingCreditScore,
  creditUsageOverviewFetched, 
  creditUsageOverviewFetchFailure, 
  creditScoreOverviewFetched, 
  creditScoreOverviewFetchFailure 
} from '../redux/slices/creditOverviewSlice';
import { CreditRiskClassification } from '../redux/companyMonitorTypes';

export interface CreditOverviewItemModel {
  customerId: string
  name: string
  companyRegistrationNumber: string
  approvedCreditLimit: Money
  availableCreditLimit: Money
  currentCreditUsage: Money
  recentMaxCreditUsage: Money
  creditScore: number | null
  creditRiskClassification: CreditRiskClassification | undefined
  probabilityOfDefaultPercent: number | null
  currentRiskedAmount: Money
  recentMaxRiskedAmount: Money
}

const useCreditOverviewActions = () => {
  const fetchClient = useFetchClient()
  const dispatch = useAppDispatch()

  const getCreditUsageOverview = (atDate: string | null) => {
    dispatch(loadingCreditUsage())   

    let url = restConfig.routes.creditUsageOverview

    if (atDate) {
      url += `?atDate=${atDate}`
    }

    return fetchClient.get(url)
      .then(response => {
          dispatch(creditUsageOverviewFetched(response.data as CreditUsageOverviewItemModel[]))
      })
      .catch(error => {
          console.log('getCreditUsageOverview error:', error)
          dispatch(creditUsageOverviewFetchFailure(error))
      })
  }

  const getCreditScoreOverview = (atDate: string | null) => {
    dispatch(loadingCreditScore())   

    let url = restConfig.routes.creditScoreOverview

    if (atDate) {
      url += `?atDate=${atDate}`
    }

    return fetchClient.get(url)
      .then(response => {
          dispatch(creditScoreOverviewFetched(response.data as CreditScoreOverviewItemModel[]))
      })
      .catch(error => {
          console.log('getCreditScoreOverview error:', error)
          dispatch(creditScoreOverviewFetchFailure(error))
      })
  }

  const combine = (usages: CreditUsageOverviewItemModel[], scores: CreditScoreOverviewItemModel[]) : CreditOverviewItemModel[] => {
    return usages.map((usage) => {
      const score = scores.find(x => x.companyRegistrationNumber === usage.companyRegistrationNumber)
      return {
        customerId: usage.customerId,
        companyRegistrationNumber: usage.companyRegistrationNumber,
        name: usage.name,
        approvedCreditLimit: usage.approvedCreditLimit,
        availableCreditLimit: usage.availableCreditLimit,
        currentCreditUsage: usage.currentCreditUsage,
        recentMaxCreditUsage: usage.recentMaxCreditUsage,
        creditScore: score?.creditScore ?? null,
        creditRiskClassification: score?.creditRiskClassification,
        probabilityOfDefaultPercent: score?.probabilityOfDefaultPercent ?? null,
        currentRiskedAmount: {
          amount: Math.round((score?.probabilityOfDefaultPercent ?? 25) * usage.currentCreditUsage.amount / 100),
          currencyCode: usage.availableCreditLimit.currencyCode
        },
        recentMaxRiskedAmount: {
          amount: Math.round((score?.probabilityOfDefaultPercent ?? 25) * usage.recentMaxCreditUsage.amount / 100),
          currencyCode: usage.availableCreditLimit.currencyCode
        }
      }
    })
  }

  return {
    getCreditUsageOverview,
    getCreditScoreOverview,
    combine
  }
}

export default useCreditOverviewActions
